import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useContext, useState, VFC } from 'react';

import deleteCreation from '@/apis/aniftyBackend/creations/deleteCreation';
import CustomButton from '@/components/CustomButton';
import FloatingModal from '@/components/FloatingModal';
import routes from '@/config/routes';
import { ProfileContext } from '@/contexts/ProfileProvider';
import useAccessToken from '@/hooks/useAccessToken';
import { captureException } from '@/utils/Sentry';

import styles from './CreationDeleteModal.module.scss';

type Props = {
  creationID: number;
  creationName: string;
  isOpen: boolean;
  onRequestClose(): void;
  onDelete?: () => void;
};

const CreationDeleteModal: VFC<Props> = ({
  creationID,
  creationName,
  isOpen,
  onRequestClose,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();

  const profileContext = useContext(ProfileContext);
  const { accessToken, requestSignature } = useAccessToken();

  const [isProcessing, setIsProcessing] = useState(false);

  const handleDeleteButtonClick = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      if (!accessToken && !(await requestSignature())) return;

      await deleteCreation(creationID);

      await router.push(routes.myPage(profileContext.userID));

      if (onDelete) {
        onDelete();
      }
    } catch (err) {
      captureException(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <FloatingModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc={!isProcessing}
      shouldCloseOnOverlayClick={!isProcessing}
    >
      <div className={styles.modalContents}>
        <div className={styles.modalTitleText}>
          <div>{t('creationDeleteModal.title')}</div>
        </div>

        <div className={styles.usage}>{t('creationDeleteModal.usage')}</div>

        <div className={styles.creationName}>
          {`${t('creationDeleteModal.creationName')} : ${creationName}`}
        </div>

        <div className={styles.buttonContainer}>
          <CustomButton
            className={styles.deleteButton}
            loading={isProcessing}
            onClick={handleDeleteButtonClick}
          >
            DELETE
          </CustomButton>

          <CustomButton
            className={styles.cancelButton}
            loading={isProcessing}
            onClick={onRequestClose}
          >
            CANCEL
          </CustomButton>
        </div>

        {isProcessing && !accessToken && (
          <div className={styles.loadingMessage}>
            {t('creationDeleteModal.loadingMessage')}
          </div>
        )}
      </div>
    </FloatingModal>
  );
};

export default CreationDeleteModal;
